import { initializeApp, getApps } from 'firebase/app'
import {
  getAuth as getFirebaseAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signInWithPopup,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  fetchSignInMethodsForEmail,
  type Auth,
  type User,
} from 'firebase/auth'

// Lazy Firebase init — evita falha em build/SSG quando env vars não estão disponíveis.
// Inicializa de fato somente no primeiro acesso a `auth.<algo>` (runtime, sempre client).
let _auth: Auth | null = null

function initFirebase(): Auth {
  if (_auth) return _auth

  const config = {
    apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY?.trim() ?? '',
    authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN?.trim() ?? '',
    projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID?.trim() ?? '',
  }

  if (!config.apiKey || !config.authDomain || !config.projectId) {
    throw new Error(
      'Firebase auth não configurado: defina NEXT_PUBLIC_FIREBASE_API_KEY, NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN e NEXT_PUBLIC_FIREBASE_PROJECT_ID',
    )
  }

  const app = getApps().length === 0 ? initializeApp(config) : getApps()[0]!
  _auth = getFirebaseAuth(app)
  return _auth
}

// Proxy correto: NÃO passa receiver pra Reflect.get (senão getters
// internos do Firebase rodam com this=Proxy e retornam undefined).
// Métodos precisam ser bind no Auth real para `this` ficar certo
// quando chamados como `auth.method()`.
export const auth = new Proxy({} as Auth, {
  get(_target, prop) {
    const real = initFirebase()
    const value = (real as unknown as Record<PropertyKey, unknown>)[prop]
    if (typeof value === 'function') {
      return (value as (...args: unknown[]) => unknown).bind(real)
    }
    return value
  },
})

export const signInWithEmail = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password)

export const signInWithGoogle = async (): Promise<{ isNewUser: boolean }> => {
  const provider = new GoogleAuthProvider()
  provider.setCustomParameters({ prompt: 'select_account' })
  const result = await signInWithPopup(auth, provider)
  const isNewUser = getAdditionalUserInfo(result)?.isNewUser ?? false
  return { isNewUser }
}

export async function registerWithEmail(name: string, email: string, password: string, phone?: string) {
  const firebaseAuth = initFirebase()
  const credential = await createUserWithEmailAndPassword(firebaseAuth, email, password)
  await updateProfile(credential.user, { displayName: name.trim() })
  await sendEmailVerification(credential.user)
  if (phone) {
    sessionStorage.setItem('reg_phone', phone.trim())
  }
  return credential
}

export async function sendPasswordReset(email: string): Promise<void> {
  const methods = await fetchSignInMethodsForEmail(auth, email)
  if (methods.length === 0) {
    const err = new Error('user-not-found') as Error & { code: string }
    err.code = 'auth/user-not-found'
    throw err
  }
  await sendPasswordResetEmail(auth, email)
}

export async function signOut() {
  clearAppToken()
  await firebaseSignOut(auth)
  document.cookie = 'auth-token=; path=/; max-age=0'
}

export const onAuthChange = (cb: (user: User | null) => void) =>
  onAuthStateChanged(auth, cb)

// ─── App JWT (troca Firebase token → JWT assinado pela API) ───────────────────

const API_URL = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001'

let appJwt: string | null = null
let pendingExchange: Promise<string | null> | null = null
let exchangeCooldownUntil = 0

export function clearAppToken() {
  appJwt = null
  pendingExchange = null
  exchangeCooldownUntil = 0
}

async function doExchange(): Promise<string | null> {
  try {
    if (Date.now() < exchangeCooldownUntil) return null

    await auth.authStateReady()
    const user = auth.currentUser
    if (!user) return null

    const firebaseToken = await user.getIdToken(false)

    // Tenta obter JWT da API
    let res = await fetch(`${API_URL}/auth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ firebaseToken }),
    })

    if (res.status === 429) {
      exchangeCooldownUntil = Date.now() + 60_000
      console.error('[Auth] Rate limited, aguardando 60s')
      return null
    }

    // 404 = usuário não existe no banco → não auto-registra (evita perda de dados)
    // O registro deve ser feito explicitamente pelo fluxo de cadastro (/cadastro)
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      console.error('[Auth] Exchange failed:', res.status, body)
      return null
    }

    const data = await res.json()
    appJwt = data.token ?? null
    return appJwt
  } catch (err) {
    console.error('[Auth] Exchange network error:', err)
    return null
  } finally {
    pendingExchange = null
  }
}

export async function getAppToken(forceRefresh = false): Promise<string | null> {
  if (appJwt && !forceRefresh) return appJwt

  // Deduplica chamadas simultâneas — forceRefresh reutiliza exchange em andamento
  if (!pendingExchange) {
    pendingExchange = doExchange()
  }
  return pendingExchange
}
